/*
 * The enforcement decisions, as pure functions.
 *
 * These live outside the host ELEMENT deliberately. The host is a Lit component
 * that needs a DOM, a backend and a frame to run at all, which would make the
 * one thing that must never be wrong - what a widget is allowed to see - the
 * hardest thing in the codebase to test. Here they are ordinary functions with
 * ordinary tests, and the element's job shrinks to calling them.
 *
 * Two decisions, made at two different moments, and BOTH are necessary:
 *
 *   - what to ASK the backend for, when a widget declares its symbols
 *   - what to FORWARD to the widget, when a frame arrives
 *
 * The second is not redundant. The request was already scoped, so in a correct
 * system every arriving value is permitted - but "the stream only ever carries
 * what we asked for" is an assumption about another service, and the cost of
 * checking it here is a set lookup. A stream that ever carried an unrequested
 * symbol would otherwise walk straight into a shared dashboard.
 */

import type { CapabilityGrant } from './capability';
import { mayRead } from './capability';

/*
 * RESOURCE BOUNDS.
 *
 * A widget is untrusted, and untrusted code that can make the host do unbounded
 * work does not need a sandbox escape to hurt anyone - it just asks for
 * everything. These are the ceilings on what one widget can cost, chosen to sit
 * far above any honest widget and far below anything that would matter.
 *
 * Every one is enforced where the request ARRIVES rather than where it is used,
 * because a bound checked late has already paid the cost it was meant to avoid.
 */

/** Symbols one widget may subscribe. A dense chart reads tens; nothing reads 500. */
export const MAX_SYMBOLS_PER_WIDGET = 256;

/** Controllers one widget may span. Each one is a separate backend stream. */
export const MAX_CONTROLLERS_PER_WIDGET = 16;

/** Write/command requests a widget may have outstanding before it is ignored. */
export const MAX_INFLIGHT_REQUESTS = 32;

/** A symbol a widget asked to read. */
export interface SymbolRef {
  controllerId: string;
  symbolId: string;
}

/**
 * The subscription plan: which symbols, grouped by controller, this context is
 * actually allowed to request. Symbols outside the grant are dropped silently -
 * the widget is never told which, because reporting the difference would let a
 * shared link enumerate the plant it was not given by asking for everything and
 * reading the refusals.
 */
export function planSubscriptions(
  grant: CapabilityGrant,
  nowMs: number,
  gatewayId: string,
  wanted: readonly SymbolRef[],
): Map<string, string[]> {
  const byController = new Map<string, string[]>();
  let total = 0;
  for (const w of wanted) {
    if (!mayRead(grant, nowMs, gatewayId, w.controllerId, w.symbolId)) continue;

    const list = byController.get(w.controllerId);
    if (list) {
      // A widget may declare the same symbol twice; subscribe to it once, and
      // do not let repetition consume the budget either.
      if (list.includes(w.symbolId)) continue;
      if (total >= MAX_SYMBOLS_PER_WIDGET) break;
      list.push(w.symbolId);
      total++;
    } else {
      if (total >= MAX_SYMBOLS_PER_WIDGET) break;
      if (byController.size >= MAX_CONTROLLERS_PER_WIDGET) continue;
      byController.set(w.controllerId, [w.symbolId]);
      total++;
    }
  }
  return byController;
}

/**
 * What a widget ACTUALLY reads, for capture into a snapshot.
 *
 * A share's grant is derived from a snapshot, and the snapshot's read list must
 * therefore be what the widget really subscribed - not what someone typed into
 * a config. A hand-authored list that OVER-declares hands a share more than the
 * dashboard ever showed, which defeats the one property the whole design rests
 * on; one that under-declares produces a share whose tiles render empty.
 *
 * Taking it from the live subscription plan makes both impossible: the captured
 * list is, by construction, the set the host was willing to deliver.
 */
export function declaredReads(plan: Map<string, string[]>): SymbolRef[] {
  const out: SymbolRef[] = [];
  for (const [controllerId, ids] of plan) {
    for (const symbolId of ids) out.push({ controllerId, symbolId });
  }
  return out;
}

/** The plan, as the membership sets the delivery filter checks against. */
export function grantedSets(plan: Map<string, string[]>): Map<string, Set<string>> {
  return new Map([...plan].map(([controllerId, ids]) => [controllerId, new Set(ids)]));
}

/**
 * Filter one batch of arriving values down to what may be forwarded. Anything
 * for an unknown controller, or a symbol not in that controller's granted set,
 * is dropped.
 */
export function filterValues<T extends { symbolId: string }>(
  granted: Map<string, Set<string>>,
  controllerId: string,
  items: readonly T[],
): T[] {
  const allowed = granted.get(controllerId);
  if (!allowed) return [];
  return items.filter((v) => allowed.has(v.symbolId));
}
